import { useQuery } from '@tanstack/react-query';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';

import type { NetworkError } from '@/errors/NetworkError';
import type { IGenericRetrieverOptions } from '@/hooks/requests/genericRetriever';

import useGenericOptions from '@/hooks/requests/useGenericOptions';
import useGenericSideEffects from '@/hooks/requests/useGenericSideEffects';

export type UseGenericRetrieverParams<Response> = {
  queryKey: QueryKey;
  url: string;
  withAuth?: boolean;
  automaticRedirect?: boolean;
  enabled?: boolean;
  extraQueryOptions?: Partial<IGenericRetrieverOptions>;
  queryOptions?: Omit<
    UseQueryOptions<Response, NetworkError, Response, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
};

function useGenericRetriever<Response>({
  queryKey,
  url,
  withAuth = true,
  automaticRedirect = true,
  enabled = true,
  queryOptions,
  extraQueryOptions,
}: UseGenericRetrieverParams<Response>) {
  const internalQueryOptions = useGenericOptions<Response>({
    url,
    withAuth,
    queryKey,
    enabled,
    extraQueryOptions,
    ...queryOptions,
  });

  const query = useQuery<Response, NetworkError, Response, QueryKey>({
    ...internalQueryOptions,
    placeholderData:
      queryOptions?.placeholderData ?? ((previousData) => previousData),
  });

  useGenericSideEffects({
    query,
    automaticRedirect,
  });

  return {
    data: query.data,
    error: query.error,
    isError: query.isError,
    isLoading: query.isLoading || query.isPlaceholderData === true,
    isFetching: query.isFetching,
    refetch: query.refetch,
    status: query.status,
    isSuccess: query.isSuccess,
  };
}

export default useGenericRetriever;
